import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { supabase } from "@/integrations/supabase/client";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/theme-provider";
import { OFFICIAL_APP_URL } from "@/lib/app-url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-7xl font-semibold text-primary">404</h1>
        <h2 className="mt-4 text-xl font-medium text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A página que você procura não existe ou foi movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Voltar ao início
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: import("@tanstack/react-router").ErrorComponentProps) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif text-2xl font-semibold text-foreground">Algo deu errado</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Tente novamente ou volte à página inicial.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Tentar novamente
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ir para o início
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Beit Tefilah Rechovot — Acesso à Área de Ensinos" },
      {
        name: "description",
        content:
          "Entre com seu login e senha para acessar os ensinos da congregação Beit Tefilah Rechovot organizados por Parashá, data, ano hebraico e festividades.",
      },
      { name: "author", content: "Beit Tefilah Rechovot" },
      { name: "theme-color", content: "#3d5f8a" },
      { name: "format-detection", content: "telephone=no" },
      { property: "og:site_name", content: "Beit Tefilah Rechovot" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: "Beit Tefilah Rechovot — Acesso à Área de Ensinos" },
      { name: "twitter:title", content: "Beit Tefilah Rechovot — Acesso à Área de Ensinos" },
      {
        property: "og:description",
        content:
          "Entre com seu login e senha para acessar os ensinos da congregação Beit Tefilah Rechovot organizados por Parashá, data, ano hebraico e festividades.",
      },
      {
        name: "twitter:description",
        content:
          "Entre com seu login e senha para acessar os ensinos da congregação Beit Tefilah Rechovot organizados por Parashá, data, ano hebraico e festividades.",
      },
      {
        property: "og:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/e389bdce-5237-4638-989c-6266ba0c69a3/id-preview-9f6e8f68--ce1b172f-33a4-453d-acfe-2d9320f3bb26.lovable.app-1785786413511.png",
      },
      {
        name: "twitter:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/e389bdce-5237-4638-989c-6266ba0c69a3/id-preview-9f6e8f68--ce1b172f-33a4-453d-acfe-2d9320f3bb26.lovable.app-1785786413511.png",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://i.ytimg.com", crossOrigin: "anonymous" },
      { rel: "dns-prefetch", href: "https://www.youtube.com" },
      {
        // Menos pesos (só os realmente usados) reduzem o tempo até o primeiro
        // texto. `display=swap` mostra o texto com a fonte do sistema enquanto
        // a fonte oficial carrega.
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Lora:wght@600;700&family=Source+Sans+3:wght@400;600&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Lora:wght@600;700&family=Source+Sans+3:wght@400;600&display=swap",
      },
    ],
    scripts: [
      {
        // Aplica o tema salvo antes da hidratação: evita o "flash" claro e a
        // divergência entre o HTML do servidor e o primeiro render no cliente.
        children:
          "try{var t=localStorage.getItem('btr-theme')||'system';var d=t==='dark'||(t==='system'&&window.matchMedia('(prefers-color-scheme: dark)').matches);document.documentElement.classList.toggle('dark',d)}catch(e){}",
      },
      {
        type: "application/ld+json",

        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": `${OFFICIAL_APP_URL}/#organization`,
              name: "Beit Tefilah Rechovot",
              alternateName: "Casa de Oração Rechovot",
              url: "https://www.rechovot.org.br/",
              sameAs: ["https://www.youtube.com/@rechovot", OFFICIAL_APP_URL],
            },
            {
              "@type": "WebSite",
              "@id": `${OFFICIAL_APP_URL}/#website`,
              name: "Área de Ensinos — Beit Tefilah Rechovot",
              url: `${OFFICIAL_APP_URL}/`,
              inLanguage: "pt-BR",
              publisher: { "@id": `${OFFICIAL_APP_URL}/#organization` },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        {/* Google Tag Manager */}
        <script
          dangerouslySetInnerHTML={{
            __html:
              "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-KQTHH9DV');",
          }}
        />
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-KQTHH9DV"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_IN" && event !== "SIGNED_OUT" && event !== "USER_UPDATED") return;
      router.invalidate();
      if (event !== "SIGNED_OUT") queryClient.invalidateQueries();
    });
    return () => sub.subscription.unsubscribe();
  }, [router, queryClient]);

  // Trilha de auditoria por usuário (ações no frontend + retorno do backend).
  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled || !data.session) return;
      const { initClientAudit } = await import("@/lib/client-audit");
      cleanup = initClientAudit({ router, queryClient });
    })();
    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [router, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Outlet />
        <Toaster richColors position="top-right" />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
